import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { percentage, CATEGORY_ICONS, CATEGORY_COLORS } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui';
import PageHeader from '@/components/shared/PageHeader';
import AddBudgetModal from '@/components/shared/AddBudgetModal';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function Budgets() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const budgets = useStore((s) => s.budgets);
  const removeBudget = useStore((s) => s.removeBudget);
  const [showModal, setShowModal] = useState(false);
  const [editBudget, setEditBudget] = useState(null);

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        title={t('budgets.title')}
        hint={t('common.hints.budgets')}
        action={
          <Button size="sm" icon={Plus} onClick={() => setShowModal(true)}>
            {t('budgets.addNew')}
          </Button>
        }
      />

      {/* Global budget summary */}
      {budgets.length > 0 && (() => {
        const totalLimit = budgets.reduce((s, b) => s + b.limit, 0);
        const totalSpent = budgets.reduce((s, b) => s + b.spent, 0);
        const globalPct = totalLimit > 0 ? Math.round((totalSpent / totalLimit) * 100) : 0;
        const overCount = budgets.filter((b) => b.spent > b.limit).length;
        const isOver = totalSpent > totalLimit;

        return (
          <div className="card bg-gradient-to-br from-brand-50 to-accent-50 dark:from-brand-500/10 dark:to-accent-500/10 border-brand-200 dark:border-brand-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('budgets.globalTotal')}
              </span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                isOver
                  ? 'bg-danger-100 dark:bg-danger-500/20 text-danger-600 dark:text-danger-400'
                  : globalPct >= 80
                    ? 'bg-warning-100 dark:bg-warning-500/20 text-warning-600 dark:text-warning-400'
                    : 'bg-success-100 dark:bg-success-500/20 text-success-600 dark:text-success-400'
              }`}>
                {globalPct}%
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300 mb-2">
              <span>{formatCurrency(totalSpent)} {t('budgets.spent')}</span>
              <span>{t('budgets.of')} {formatCurrency(totalLimit)}</span>
            </div>
            <div className="h-3 bg-white/60 dark:bg-gray-800/60 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(globalPct, 100)}%`,
                  backgroundColor: isOver ? '#ef4444' : globalPct >= 80 ? '#f59e0b' : '#3b82f6',
                }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{formatCurrency(Math.max(totalLimit - totalSpent, 0))} {t('budgets.remaining')}</span>
              {overCount > 0 && (
                <span className="text-danger-500">{overCount} {t('budgets.overBudget').toLowerCase()}</span>
              )}
            </div>
          </div>
        );
      })()}

      <div className="space-y-3">
        {budgets.map((b) => {
          const pct = percentage(b.spent, b.limit);
          const over = b.spent > b.limit;
          const color = CATEGORY_COLORS[b.category] || '#94a3b8';

          return (
            <div key={b.id} className="card">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-lg">{CATEGORY_ICONS[b.category]}</span>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {t(`categories.${b.category}`)}
                    </span>
                    {over && (
                      <span className="badge-expense text-xs">
                        {t('budgets.overBudget')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatCurrency(b.spent)} {t('budgets.spent')} {t('budgets.of')} {formatCurrency(b.limit)}
                  </div>
                </div>
              </div>
              <div className="h-3 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    backgroundColor: over ? '#ef4444' : color,
                  }}
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                <div className="text-xs text-gray-500">
                  {formatCurrency(Math.max(b.limit - b.spent, 0))} {t('budgets.remaining')}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditBudget(b); setShowModal(true); }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Pencil size={14} className="text-gray-400" />
                  </button>
                  <button
                    onClick={() => removeBudget(b.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <Trash2 size={14} className="text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AddBudgetModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditBudget(null); }}
        budget={editBudget}
      />
    </div>
  );
}
